import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ConvergingLines } from "@/components/ConvergingLines";
import { projects } from "@/content/siteContent";

const Section = ({ title, children, testId }) => (
  <Reveal>
    <section className="border-t border-ink/10 py-12" data-testid={testId}>
      <h2 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
      <div className="mt-6 max-w-3xl">{children}</div>
    </section>
  </Reveal>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug && p.caseStudy);

  if (!project) return <Navigate to="/work" replace />;

  const cs = project.caseStudy;

  return (
    <div data-testid="case-study-page">
      <header className="relative overflow-hidden bg-sand">
        <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-16 sm:pt-24">
          <div className="hidden sm:block">
            <ConvergingLines className="pointer-events-none absolute inset-0 h-full w-full" testId="case-study-motif" />
          </div>
          <Reveal>
            <Link
              to="/work"
              className="editorial-link inline-flex items-center gap-2 text-sm font-medium text-muted"
              data-testid="case-study-back-link"
            >
              <ArrowLeft size={16} aria-hidden="true" /> All work
            </Link>
            <div className="mt-12">
              <p className="eyebrow whitespace-nowrap">{project.type}</p>
            </div>
            <h1
              className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-tighter sm:text-5xl"
              data-testid="case-study-title"
            >
              {project.title}
              {project.org && project.type === "Corporate case study" && (
                <span className="text-muted"> — {project.org}</span>
              )}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
              {project.summary}
            </p>
            {cs.note && (
              <p className="placeholder-note mt-8 max-w-2xl" data-testid="case-study-note">
                {cs.note}
              </p>
            )}
            {project.links && (
              <ul className="mt-8 flex flex-wrap gap-4" data-testid="case-study-links">
                {project.links.map((l) => (
                  <li key={l.url}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="editorial-link inline-flex items-center gap-2 text-sm font-semibold"
                      data-testid={`case-study-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label} <ArrowUpRight size={16} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Reveal>
        </div>
      </header>

      <div className="bg-white">
        <div className="mx-auto max-w-6xl px-6 pb-20">
          {cs.sections.map((s, i) => (
            <Section
              key={s.title}
              title={s.title}
              testId={`cs-section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {s.text &&
                (Array.isArray(s.text) ? (
                  s.text.map((p) => (
                    <p key={p} className="mt-4 text-base leading-relaxed text-muted first:mt-0">
                      {p}
                    </p>
                  ))
                ) : (
                  <p className="text-base leading-relaxed text-muted">{s.text}</p>
                ))}
              {s.items && (
                <ul className="list-disc space-y-3 pl-5 text-base leading-relaxed text-muted">
                  {s.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {s.placeholder && (
                <p className="placeholder-note mt-4" data-testid="cs-placeholder-item">
                  {s.placeholder}
                </p>
              )}
            </Section>
          ))}

          <Section title="Capabilities demonstrated" testId="cs-capabilities">
            <ul className="flex flex-wrap gap-2">
              {cs.capabilities.map((c) => (
                <li key={c} className="rounded-full border border-ink/20 px-4 py-1.5 text-sm text-muted">
                  {c}
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}
